/**
 * Global Utility to Disable Scroll-to-Change on Number Input Fields
 * 
 * This utility prevents accidental value changes when scrolling over number input fields.
 * It uses event delegation to handle all number inputs, including dynamically added ones.
 * 
 * The scroll-to-change behavior is a default browser feature for number inputs that
 * allows users to increment/decrement values by scrolling. This can cause accidental
 * changes when users scroll the page while a number input is focused.
 */

/**
 * Initialize the scroll prevention for all number inputs
 * This should be called once when the app loads
 */
export const initDisableNumberInputScroll = () => {
  // Function to handle wheel events on number inputs
  // This prevents the default scroll-to-change behavior
  const handleWheel = (e) => {
    // Check if the target is a number input AND it's currently focused
    // This ensures we only prevent scroll-to-change when the input is active,
    // not when the user is just scrolling the page and the mouse happens to be over an input
    const target = e.target;
    if (target && target.type === 'number' && document.activeElement === target) {
      // Prevent the default scroll behavior that changes the value
      e.preventDefault();
      // Stop the event from propagating to prevent any other handlers
      e.stopPropagation();
      // Return false as additional safeguard
      return false;
    }
  };

  // Add event listener to document for event delegation
  // This will catch all number inputs, including dynamically added ones
  // Using capture phase to catch events early
  // passive: false is required to allow preventDefault()
  document.addEventListener('wheel', handleWheel, { passive: false, capture: true });
  
  // Also handle mousewheel for older browsers
  document.addEventListener('mousewheel', handleWheel, { passive: false, capture: true });
  
  // Handle DOMMouseScroll for Firefox
  document.addEventListener('DOMMouseScroll', handleWheel, { passive: false, capture: true });

  // Return cleanup function (optional, for if we need to remove listeners)
  return () => {
    document.removeEventListener('wheel', handleWheel, { capture: true });
    document.removeEventListener('mousewheel', handleWheel, { capture: true });
    document.removeEventListener('DOMMouseScroll', handleWheel, { capture: true });
  };
};

/**
 * Alternative approach: Add onWheel handler directly to number inputs
 * This can be used as a React prop on Input components
 */
export const disableNumberInputScrollProps = {
  onWheel: (e) => {
    // Prevent scroll from changing the value
    e.target.blur();
  },
  onFocus: (e) => {
    // Optional: Select all text on focus for easier editing
    // e.target.select();
  }
};
